import { Sparkles } from 'lucide-react'
import { getMinCopy } from '@/i18n/min'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { getGuestLocale, isGuestLocale } from '@/lib/guestLocale'
import { useSettingsStore } from '@/store/settingsStore'
import { AI_CREDIT_COSTS, formatCreditCost, formatCreditDebit } from '@/lib/aiCredits'
import { useAiCredits } from '@/hooks/useAiCredits'
import { cn } from '@/components/ui/cn'
import type { UiLocale } from '@/i18n/types'

interface AiCreditsIndicatorProps {
  compact?: boolean
  inline?: boolean
  className?: string
}

function resolveUiLocale(): UiLocale {
  const uiLanguage = useSettingsStore.getState().settings?.uiLanguage
  if (isGuestLocale(uiLanguage)) return uiLanguage
  return getGuestLocale()
}

function formatResetTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    }).format(new Date(iso))
  } catch {
    return 'midnight UTC'
  }
}

export function AiCreditCostBadge({
  cost,
  className,
}: {
  cost: number
  className?: string
}) {
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground',
        className
      )}
    >
      {formatCreditDebit(cost)}
    </span>
  )
}

export function AiCreditsIndicator({ compact = false, inline = false, className }: AiCreditsIndicatorProps) {
  const { aiCredits } = useMinCopy()
  const { usage: data, isLoading, isError } = useAiCredits()

  if (isLoading || isError || !data) return null
  if (data.tier === 'ADMIN' || data.creditsLimit < 0) return null

  const pct =
    data.creditsLimit > 0
      ? Math.min(100, Math.round((data.creditsUsed / data.creditsLimit) * 100))
      : 0
  const remainingPct =
    data.creditsLimit > 0
      ? Math.min(100, Math.round((data.creditsRemaining / data.creditsLimit) * 100))
      : 0
  const low = data.creditsRemaining <= 10
  const exhausted = data.creditsRemaining <= 0

  if (inline) {
    return (
      <div
        className={cn('border-t border-border/60 pt-3', className)}
        data-testid="ai-credits-inline"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="text-xs text-muted-foreground">{aiCredits.short}</span>
          </div>
          <span
            className={cn(
              'text-xs font-semibold tabular-nums',
              exhausted && 'text-destructive',
              low && !exhausted && 'text-amber-600 dark:text-amber-400'
            )}
          >
            {data.creditsRemaining}
            <span className="font-normal text-muted-foreground"> / {data.creditsLimit}</span>
          </span>
        </div>
        <div className="mt-2 h-1 w-full rounded-full bg-secondary">
          <div
            className={cn(
              'h-1 rounded-full transition-all',
              exhausted ? 'bg-destructive' : low ? 'bg-amber-500' : 'bg-primary'
            )}
            style={{ width: `${remainingPct}%` }}
          />
        </div>
      </div>
    )
  }

  if (compact) {
    return (
      <p
        className={cn(
          'text-xs text-muted-foreground',
          low && !exhausted && 'text-amber-600 dark:text-amber-400',
          exhausted && 'text-destructive',
          className
        )}
        data-testid="ai-credits-compact"
      >
        {formatMessage(aiCredits.compact, {
          remaining: data.creditsRemaining,
          limit: data.creditsLimit,
        })}
      </p>
    )
  }

  return (
    <div
      className={cn('rounded-xl border border-border/60 bg-muted/10 p-4', className)}
      data-testid="ai-credits-indicator"
    >
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">{aiCredits.heading}</p>
      </div>

      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span
          className={cn(
            'text-2xl font-bold tabular-nums',
            exhausted && 'text-destructive',
            low && !exhausted && 'text-amber-600 dark:text-amber-400'
          )}
        >
          {data.creditsRemaining}
        </span>
        <span className="text-sm text-muted-foreground">
          {formatMessage(aiCredits.ofLimit, {
            used: data.creditsUsed,
            limit: data.creditsLimit,
          })}
        </span>
      </div>

      <div className="mb-3 h-2 w-full rounded-full bg-secondary">
        <div
          className={cn(
            'h-2 rounded-full transition-all',
            exhausted ? 'bg-destructive' : low ? 'bg-amber-500' : 'bg-primary'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="mb-3 space-y-1 text-xs text-muted-foreground">
        <li>
          {formatMessage(aiCredits.costPractice, {
            cost: formatCreditCost(AI_CREDIT_COSTS.practiceGenerate),
          })}
        </li>
        <li>
          {formatMessage(aiCredits.costArticle, {
            cost: formatCreditCost(AI_CREDIT_COSTS.articleGenerate),
          })}
        </li>
        <li>
          {formatMessage(aiCredits.costComprehension, {
            cost: formatCreditCost(AI_CREDIT_COSTS.comprehensionGenerate),
          })}
        </li>
        <li>
          {formatMessage(aiCredits.costComprehensionCheck, {
            cost: formatCreditCost(AI_CREDIT_COSTS.comprehensionCheck),
          })}
        </li>
        {data.phraseExplanationsLimit >= 0 && (
          <li>
            {formatMessage(aiCredits.phraseQuotaCompact, {
              remaining: data.phraseExplanationsRemaining,
              limit: data.phraseExplanationsLimit,
            })}
          </li>
        )}
      </ul>

      <p className="text-xs leading-relaxed text-muted-foreground">{aiCredits.hint}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {formatMessage(aiCredits.reset, { time: formatResetTime(data.resetsAt) })}
      </p>
    </div>
  )
}

export function aiCreditsExceededMessage(message?: string): string {
  return message ?? getMinCopy(resolveUiLocale()).aiCredits.exceeded
}

export function phraseLimitExceededMessage(message?: string): string {
  return message ?? getMinCopy(resolveUiLocale()).aiCredits.phraseLimitExceeded
}
